import React, { useContext, useEffect, useState } from "react";
import { useMessageSource } from "react-message-source";
import BasicAccountApplicationAppliedItem from "../../../../ui/widgets/basic-account-application-item/applied";
import CustomLink from "../../../../ui/widgets/link";
import {
  InsurancePlusIcon,
  InvoicingTypeIcon,
  SignatureIcon,
} from "../../../../ui/widgets/icons";
import { createApplicationPdfLink } from "../../../../util/pdf";
import ProductImage from "../../../shared/components/product-image";
import { AppliedProductProps } from "./types";
import { ProductContext } from "../../../../context/product-context/productContext";
import FloatingActionButton from "../../../../ui/widgets/floating-action-button";
import { ProductRefType } from "../../../../context/product-context/types";
import { ApplicationStatus } from "../../../shared/types";
import { useFeatureToggles } from "../../../shared/hooks/useFeatureToggles";
import { Feature } from "../../../../context/types";
import CustomTypography from "../../../../ui/widgets/typography";
import useInsurancePlus from "../../../shared/hooks/useInsurancePlus";

export default function AppliedProduct({
  startCardApplication,
  startBasicAccountApplication,
  appliedApplication,
  openProductSelector,
  openCsDisabledDialog,
  isDisabled,
}: AppliedProductProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { setSelectedProduct, findProduct, findPersistedProductByRef } =
    useContext(ProductContext);
  const { doesProdcutHaveInsurancePlus } = useInsurancePlus();

  const canCreateMultipleBaaPerProduct = useFeatureToggles(
    Feature.MULTIPLE_BASIC_ACCOUNT_APPLICATIONS_PER_PRODUCT,
  );

  const [basicApplicationPdfLink, setBasicApplicationPdfLink] = useState<
    string | null
  >(null);

  const {
    applicationRef,
    productRef,
    companyName,
    // @ts-expect-error - It exists. Problem with boolean mapping between backend and frontend.
    soleProprietorship,
    hasDecentralizedInvoicing,
    customizedCard,
    status,
    limit,
  } = appliedApplication;

  const product = findPersistedProductByRef(productRef as ProductRefType);
  const hasInsurancePlus = doesProdcutHaveInsurancePlus(product);
  const isCreditSuisseProduct =
    product.family.name === "CS_DUO" || product.family.name === "CS_SINGLE";

  useEffect(() => {
    const run = async () => {
      const basicApplicationPdfLink =
        await createApplicationPdfLink(applicationRef);
      setBasicApplicationPdfLink(basicApplicationPdfLink);
    };
    run();
  }, [applicationRef]);

  const userIcon = <i className="icon-scd-user" />;

  const startCardApplicationButton = (
    <FloatingActionButton
      text={getMessage("Dashboard.basicAccountApplicationApplied.button-text")}
      onClick={() => {
        if (isDisabled && isCreditSuisseProduct) {
          openCsDisabledDialog();
        } else {
          setSelectedProduct(findProduct(productRef as ProductRefType));
          startCardApplication(
            applicationRef,
            productRef,
            companyName,
            soleProprietorship,
            hasDecentralizedInvoicing,
            limit,
          );
        }
      }}
    >
      {userIcon}
    </FloatingActionButton>
  );

  const startBaaButton = (
    <FloatingActionButton
      kind="add"
      text={getMessage("Dashboard.basicAccountApplicationOpen.button-text")}
      onClick={() => {
        if (isDisabled && isCreditSuisseProduct) {
          openProductSelector();
        } else {
          setSelectedProduct(findProduct(productRef as ProductRefType));
          startBasicAccountApplication(productRef as ProductRefType);
        }
      }}
    />
  );

  const pdfLink = (
    <CustomLink
      noicon
      href={basicApplicationPdfLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      {getMessage("Dashboard.basicAccountApplicationApplied.pdf-link")}
    </CustomLink>
  );

  const signature = (
    <>
      <SignatureIcon type={status as ApplicationStatus} />
      <span className="item-text">
        {getMessage(
          `Dashboard.basicAccountApplication-signature-${status.toLowerCase()}`,
        )}
      </span>
    </>
  );

  const productImage = (
    <ProductImage
      product={productRef as ProductRefType}
      isCustomized={
        canCreateMultipleBaaPerProduct &&
        customizedCard &&
        customizedCard !== "NONE"
      }
    />
  );

  const invoicingType = () => {
    const invoicingTypeKey = hasDecentralizedInvoicing
      ? "Dashboard.basicAccountApplicationApplied.decentralizedInvoicing.text"
      : "Dashboard.basicAccountApplicationApplied.centralizedInvoicing.text";

    return (
      hasDecentralizedInvoicing !== null && (
        <>
          <InvoicingTypeIcon
            hasDecentralizedInvoicing={hasDecentralizedInvoicing}
          />
          <span className="item-text">{getMessage(invoicingTypeKey)}</span>
        </>
      )
    );
  };

  const insurancePlus = () => {
    return (
      hasInsurancePlus && (
        <>
          <InsurancePlusIcon />
          <span className="item-text">
            {getMessage(
              `Dashboard.basicAccountApplicationApplied.insurancePlus.text`,
            )}
          </span>
        </>
      )
    );
  };

  const additionalInfoFeatures = canCreateMultipleBaaPerProduct
    ? {
        invoicingType: invoicingType(),
        insurancePlus: insurancePlus(),
      }
    : {};

  const appliedItemTextKey =
    isDisabled && !isCreditSuisseProduct
      ? "Dashboard.basicAccountApplicationApplied.disabled.text"
      : "Dashboard.basicAccountApplicationApplied.text";

  const cardActionsItem = () => {
    return (
      <>
        {isDisabled && !isCreditSuisseProduct ? (
          <CustomTypography variant="body2">
            {getMessage("Dashboard.disabledProduct.text")}
          </CustomTypography>
        ) : (
          <>
            {canCreateMultipleBaaPerProduct && startBaaButton}{" "}
            {startCardApplicationButton}
          </>
        )}
      </>
    );
  };

  return (
    <BasicAccountApplicationAppliedItem
      text={getMessage(appliedItemTextKey)}
      pdfLink={pdfLink}
      product={productRef as ProductRefType}
      image={productImage}
      title={getMessage(`Product.${productRef as ProductRefType}.title`)}
      cardActionsItem={cardActionsItem()}
      signature={signature}
      {...additionalInfoFeatures}
    />
  );
}
